import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { dialog, drawer, toast } from "@strust/ui/recipes";
import {
  AlertTriangleIcon,
  CircleCheckIcon,
  InfoIcon,
  OctagonXIcon,
  XIcon,
} from "../icons/icons.tsx";
import { Button } from "../primitives/button.tsx";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]",
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(", ");

/** Moves focus into `panel`, traps Tab inside it and returns focus to the opener on close. */
function useFocusTrap(open: boolean, panel: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();
    return () => {
      opener?.focus();
    };
  }, [open, panel]);

  return (event: KeyboardEvent, onClose: () => void) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !panel.current) return;
    const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

export type DialogTone = "default" | "danger" | "warning";

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  /** Short context under the title (e.g. "alice-7f2c · production"). */
  readonly subtitle?: ReactNode;
  /** Glyph in the head (an icon); its color follows `tone`. */
  readonly icon?: ReactNode;
  readonly tone?: DialogTone;
  readonly size?: "sm" | "md" | "lg";
  readonly onClose: () => void;
  /** Label of the footer's cancel/close button (also closes with Escape). */
  readonly closeLabel: string;
  readonly children?: ReactNode;
  /** Action buttons rendered in the footer after the close button (confirm). */
  readonly actions?: ReactNode;
}

/**
 * Modal dialog: head with glyph and title, body, footer on surface.2. Focus moves in, stays
 * trapped, Escape closes, focus returns to the opener. The head's × is a mouse shortcut of the
 * footer's close button (hidden from assistive technology and the tab order).
 */
export function Dialog({
  open,
  title,
  subtitle,
  icon,
  tone = "default",
  size = "md",
  onClose,
  closeLabel,
  children,
  actions,
}: DialogProps) {
  const titleId = useId();
  const subtitleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const onKeyDown = useFocusTrap(open, panel);
  const s = dialog({ tone, size });

  if (!open) return null;

  return createPortal(
    <div
      className={s.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        className={s.panel}
        onKeyDown={(event) => {
          onKeyDown(event, onClose);
        }}
      >
        <div className={s.head}>
          {icon ? (
            <span aria-hidden="true" className={s.glyph}>
              {icon}
            </span>
          ) : null}
          <div className={s.titles}>
            <h2 id={titleId} className={s.title}>
              {title}
            </h2>
            {subtitle ? (
              <p id={subtitleId} className={s.subtitle}>
                {subtitle}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className={s.close}
            tabIndex={-1}
            aria-hidden="true"
            title={closeLabel}
            onClick={onClose}
          >
            <XIcon />
          </button>
        </div>
        {children !== undefined ? <div className={s.body}>{children}</div> : null}
        <div className={s.foot}>
          <Button visual={actions ? "ghost" : "secondary"} onClick={onClose}>
            {closeLabel}
          </Button>
          {actions}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly icon?: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** Destructive actions use the danger style and glyph. */
  readonly destructive?: boolean;
  /** While true the confirm button is busy and cannot be pressed twice. */
  readonly pending?: boolean;
  /** Explanation of the consequences and, after a refusal, the error message. */
  readonly children?: ReactNode;
}

/** Confirmation of a sensitive action: explains it, offers cancel and confirm. */
export function ConfirmDialog({
  open,
  title,
  subtitle,
  icon,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  destructive = false,
  pending = false,
  children,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      title={title}
      {...(subtitle === undefined ? {} : { subtitle })}
      icon={icon ?? (destructive ? <AlertTriangleIcon /> : <InfoIcon />)}
      tone={destructive ? "danger" : "default"}
      size="sm"
      onClose={onCancel}
      closeLabel={cancelLabel}
      actions={
        <Button visual={destructive ? "danger" : "primary"} loading={pending} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      }
    >
      {children}
    </Dialog>
  );
}

export interface DrawerProps {
  readonly open: boolean;
  readonly title: string;
  readonly overline?: ReactNode;
  readonly onClose: () => void;
  /** Accessible name of the close button. */
  readonly closeLabel: string;
  readonly children: ReactNode;
  /**
   * `modal` (default): an overlay drawer with a backdrop and focus trap. `inline`: a side panel in
   * the page flow (e.g. next to a map) without trapping focus.
   */
  readonly mode?: "modal" | "inline";
}

/** Side panel: overline, title, close button and a scrolling body. */
export function Drawer({
  open,
  title,
  overline,
  onClose,
  closeLabel,
  children,
  mode = "modal",
}: DrawerProps) {
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  const onKeyDown = useFocusTrap(open && mode === "modal", panel);
  const s = drawer({ inline: mode === "inline" });
  if (!open) return null;

  const content = (
    <section
      ref={panel}
      className={s.panel}
      aria-labelledby={titleId}
      {...(mode === "modal" ? { role: "dialog", "aria-modal": true, tabIndex: -1 } : {})}
      onKeyDown={(event) => {
        if (mode === "modal") onKeyDown(event, onClose);
        else if (event.key === "Escape") onClose();
      }}
    >
      <div className={s.head}>
        <div className={s.titles}>
          {overline ? <p className={s.overline}>{overline}</p> : null}
          <h2 id={titleId} className={s.title}>
            {title}
          </h2>
        </div>
        <button
          type="button"
          className={s.close}
          aria-label={closeLabel}
          title={closeLabel}
          onClick={onClose}
        >
          <XIcon aria-hidden="true" />
        </button>
      </div>
      <div className={s.body}>{children}</div>
    </section>
  );

  if (mode === "inline") return content;
  return createPortal(
    <>
      <div className={s.backdrop} aria-hidden="true" onMouseDown={onClose} />
      {content}
    </>,
    document.body,
  );
}

export type ToastTone = "success" | "error" | "info";

interface ToastEntry {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
}

interface ToastApi {
  readonly show: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TOAST_ICONS: Record<ToastTone, ReactNode> = {
  success: <CircleCheckIcon />,
  error: <OctagonXIcon />,
  info: <InfoIcon />,
};

/**
 * Toast region (bottom end): messages are announced politely (errors assertively) and dismissed
 * after `durationMs` or with their close button.
 */
export function ToastProvider({
  children,
  closeLabel,
  durationMs = 6000,
}: {
  readonly children: ReactNode;
  /** Accessible name of each toast's close button (from i18n). */
  readonly closeLabel: string;
  readonly durationMs?: number;
}) {
  const [entries, setEntries] = useState<readonly ToastEntry[]>([]);
  const nextId = useRef(1);
  const dismiss = useCallback((id: number) => {
    setEntries((current) => current.filter((entry) => entry.id !== id));
  }, []);
  const show = useCallback(
    (message: string, tone: ToastTone = "info") => {
      const id = nextId.current++;
      setEntries((current) => [...current, { id, tone, message }]);
      setTimeout(() => {
        dismiss(id);
      }, durationMs);
    },
    [dismiss, durationMs],
  );
  const api = useMemo(() => ({ show }), [show]);
  const s = toast();
  return (
    <ToastContext.Provider value={api}>
      {children}
      <ol className={s.region}>
        {entries.map((entry) => {
          const t = toast({ tone: entry.tone });
          return (
            <li key={entry.id}>
              <div className={t.root} role={entry.tone === "error" ? "alert" : "status"}>
                <span aria-hidden="true" className={t.rule} />
                <span aria-hidden="true" className={t.icon}>
                  {TOAST_ICONS[entry.tone]}
                </span>
                <p className={t.message}>{entry.message}</p>
                <button
                  type="button"
                  className={t.close}
                  aria-label={closeLabel}
                  title={closeLabel}
                  onClick={() => {
                    dismiss(entry.id);
                  }}
                >
                  <XIcon aria-hidden="true" />
                </button>
              </div>
            </li>
          );
        })}
      </ol>
    </ToastContext.Provider>
  );
}

/** Shows a toast; outside a `ToastProvider` it does nothing. */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NO_TOASTS;
}

const NO_TOASTS: ToastApi = {
  show: () => {
    /* no ToastProvider mounted */
  },
};
